import React, { useState } from 'react';
import {
  useFieldArray,
  UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';
import styles from '../../../DataExtractionTemplate.module.scss';
import outcomeStyles from '../OutcomesPanel.module.scss';
import EditableField from '../../EditableField';
import ConfirmationDialog from '../../ConfirmationDialog';
import { EditableField as EditableFieldType } from 'pages/DataExtraction/components/editableFieldValidation';
import {
  Checkbox,
  FormControl,
  Heading,
  Icon,
  IconList,
  Link,
  Text,
} from 'components/core';
import { useHidingDefaultFields } from 'pages/DataExtraction/DataExtractionTemplate/useHidingDefaultFields';
import { HiddenFieldCategory } from 'types/DataExtraction';
import { DataExtractionTemplateState } from 'pages/DataExtraction/DataExtractionTemplate/DataExtractionTemplate';
import { useReviewFeaturesContext } from 'contexts/ReviewFeaturesProvider';
import {
  validate,
  ErrorMessages,
  LabelErrorMessages,
} from 'pages/DataExtraction/components/editableFieldValidation';

const DEFAULT_OUTCOME_DETAILS = [
  { name: 'scale' },
  { name: 'range' },
  { name: 'unit of measurement' },
  { name: 'direction' },
  { name: 'data value' },
  { name: 'notes' },
];
const MAX_NAME_LENGTH = 255;

const customErrorMessages: ErrorMessages = {
  ...LabelErrorMessages,
  uniqueValueError:
    'Already included in outcome details. Please enter a unique label.',
};

const OutcomeDetails = ({
  showHeading,
  fullWidth,
}: {
  showHeading?: boolean;
  fullWidth?: boolean;
}) => {
  const outcomeDetails = useFieldArray<DataExtractionTemplateState>({
    name: 'outcomes.outcome_details',
  }) as UseFieldArrayReturn<
    DataExtractionTemplateState,
    'outcomes.outcome_details'
  >;
  const [modal, setModal] = useState<React.ReactElement | null>();
  const { getValues } = useFormContext();
  const { hasFeature } = useReviewFeaturesContext();
  const { handleChange, isChecked } = useHidingDefaultFields(
    HiddenFieldCategory.OUTCOMES
  );
  const templateHasExtractedData = getValues('has_extracted_data');

  const deleteDetailWithConfirmation = (index: number) => {
    if (templateHasExtractedData) {
      setModal(
        <ConfirmationDialog
          onConfirm={() => outcomeDetails.remove(index)}
          onClose={() => setModal(null)}
        />
      );
    } else {
      outcomeDetails.remove(index);
    }
  };

  const validateOutcomeDetail = (
    value: string,
    customOutcomeDetails: EditableFieldType[]
  ) => {
    const allOutcomeDetails = [
      ...DEFAULT_OUTCOME_DETAILS,
      ...customOutcomeDetails,
    ];
    return validate(
      value,
      allOutcomeDetails,
      [],
      null,
      MAX_NAME_LENGTH,
      customErrorMessages
    );
  };

  return (
    <div className={styles.details}>
      {modal}
      {showHeading && (
        <>
          <Heading type="h5" component="h4" variant="neutral">
            Outcome details
          </Heading>
          <Text variant="weak" className={outcomeStyles.outcomeDetailsText}>
            For each outcome reported in the study, what outcome details do you
            want to collect?
          </Text>
        </>
      )}

      <ul>
        <li>
          <FormControl>
            <Checkbox
              name="scale"
              value="scale"
              checked={isChecked('scale')}
              onChange={handleChange}
            >
              <Text variant="medium">Scale</Text>
            </Checkbox>
          </FormControl>
        </li>
        <li>
          <FormControl>
            <Checkbox
              name="range"
              value="range"
              checked={isChecked('range')}
              onChange={handleChange}
            >
              <Text variant="medium">Range</Text>
            </Checkbox>
          </FormControl>
        </li>
        <li>
          <FormControl>
            <Checkbox
              name="unit_of_measurement"
              value="unit_of_measurement"
              checked={isChecked('unit_of_measurement')}
              onChange={handleChange}
            >
              <Text variant="medium">Unit of measurement</Text>
            </Checkbox>
          </FormControl>
        </li>
        <li>
          <FormControl>
            <Checkbox
              name="direction"
              value="direction"
              checked={isChecked('direction')}
              onChange={handleChange}
            >
              <Text variant="medium">Direction</Text>
            </Checkbox>
          </FormControl>
        </li>
        <li>
          <FormControl>
            <Checkbox
              name="data_value"
              value="data_value"
              checked={isChecked('data_value')}
              onChange={handleChange}
            >
              <Text variant="medium">Data value</Text>
            </Checkbox>
          </FormControl>
        </li>
        <li>
          <FormControl>
            <Checkbox
              name="notes"
              value="notes"
              checked={isChecked('notes')}
              onChange={handleChange}
            >
              <Text variant="medium">Notes</Text>
            </Checkbox>
          </FormControl>
        </li>
      </ul>
      {hasFeature('template_workflow') &&
        outcomeDetails.fields.map((outcomeDetail, index) => {
          return (
            <EditableField
              key={outcomeDetail.id}
              index={index}
              fullWidth={fullWidth}
              name="outcomes.outcome_details"
              onDelete={() => deleteDetailWithConfirmation(index)}
              validate={validateOutcomeDetail}
            />
          );
        })}
      {hasFeature('template_workflow') && (
        <Link
          onClick={() => outcomeDetails.append({ name: '' })}
          variant="primary"
          className={outcomeStyles.AddOutcomeDetail}
        >
          <Icon icon={IconList.light.faPlus} /> Add detail
        </Link>
      )}
    </div>
  );
};

export default OutcomeDetails;
